import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, addDoc, serverTimestamp, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { FaUserPlus, FaTrash, FaQrcode, FaClock, FaMapMarkerAlt, FaUserTie } from 'react-icons/fa';

function AsistenciaAdminView() {
    const [empleados, setEmpleados] = useState([]);
    const [registros, setRegistros] = useState([]);
    const [nuevoEmpleado, setNuevoEmpleado] = useState({ nombre: '', cargo: '', pin: '' });

    // Cargar empleados y registros
    useEffect(() => {
        const unsubEmpleados = onSnapshot(collection(db, 'empleados_asistencia'), (snapshot) => {
            setEmpleados(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const qRegistros = query(collection(db, 'registros_asistencia'), orderBy('timestamp', 'desc'));
        const unsubRegistros = onSnapshot(qRegistros, (snapshot) => {
            setRegistros(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => { unsubEmpleados(); unsubRegistros(); };
    }, []);

    const handleAgregarEmpleado = async (e) => {
        e.preventDefault();
        if (!nuevoEmpleado.nombre || !nuevoEmpleado.pin || nuevoEmpleado.pin.length !== 4) {
            return toast.error("Ingresa nombre y un PIN de exactamente 4 dígitos.");
        }
        try {
            await addDoc(collection(db, 'empleados_asistencia'), {
                ...nuevoEmpleado,
                createdAt: serverTimestamp()
            });
            toast.success("Trabajador registrado.");
            setNuevoEmpleado({ nombre: '', cargo: '', pin: '' });
        } catch (error) {
            toast.error("Error al registrar.");
            console.error(error);
        }
    };

    const handleEliminarEmpleado = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar a este trabajador del sistema?")) {
            await deleteDoc(doc(db, 'empleados_asistencia', id));
            toast.success("Trabajador eliminado.");
        }
    };

    // 🔥 JARVIS: FUNCIÓN PARA BORRAR REGISTROS INDIVIDUALES (ENTRADAS/SALIDAS)
    const handleEliminarRegistro = async (id) => {
        if (window.confirm("¿Estás seguro de que deseas borrar este registro de fichaje? Esta acción no se puede deshacer.")) {
            try {
                await deleteDoc(doc(db, 'registros_asistencia', id));
                toast.success("Registro de asistencia eliminado.");
            } catch (error) {
                toast.error("Error al eliminar el registro.");
                console.error(error);
            }
        }
    };

    // Generamos la URL del QR dinámicamente apuntando a tu ruta de fichaje
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(window.location.origin + '/fichaje')}`;

    return (
        <div className="p-4 sm:p-6 bg-gray-50 min-h-full">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* COLUMNA IZQUIERDA: GESTIÓN Y QR */}
                <div className="lg:col-span-1 space-y-6">
                    {/* Tarjeta del QR Maestro */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
                        <h2 className="text-lg font-bold text-[#2C3E50] mb-2 flex items-center justify-center gap-2">
                            <FaQrcode /> QR del Taller
                        </h2>
                        <p className="text-xs text-gray-500 mb-4">Imprime este código y pégalo en la entrada. Los trabajadores lo escanearán con su móvil.</p>
                        <div className="flex justify-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <img src={qrUrl} alt="QR Fichaje" className="w-48 h-48" />
                        </div>
                        <a href={qrUrl} target="_blank" rel="noreferrer" className="mt-4 block w-full py-2 bg-[#18BC9C] text-white rounded-md font-bold text-sm hover:bg-teal-500 transition-colors">
                            Descargar QR
                        </a>
                    </div>

                    {/* Agregar Trabajador */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-lg font-bold text-[#2C3E50] mb-4 flex items-center gap-2">
                            <FaUserPlus /> Nuevo Trabajador
                        </h2>
                        <form onSubmit={handleAgregarEmpleado} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">Nombre Completo</label>
                                <input type="text" value={nuevoEmpleado.nombre} onChange={e => setNuevoEmpleado({...nuevoEmpleado, nombre: e.target.value})} className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-[#18BC9C] outline-none" placeholder="Ej: Carlos Pérez" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">Cargo / Puesto</label>
                                <input type="text" value={nuevoEmpleado.cargo} onChange={e => setNuevoEmpleado({...nuevoEmpleado, cargo: e.target.value})} className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-[#18BC9C] outline-none" placeholder="Ej: Instalador" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">PIN Secreto (4 dígitos)</label>
                                <input type="text" maxLength="4" value={nuevoEmpleado.pin} onChange={e => setNuevoEmpleado({...nuevoEmpleado, pin: e.target.value.replace(/\D/g, '')})} className="w-full p-2 border rounded-md text-center text-xl font-bold tracking-widest focus:ring-2 focus:ring-[#18BC9C] outline-none" placeholder="****" />
                            </div>
                            <button type="submit" className="w-full py-2 bg-[#2C3E50] text-white rounded-md font-bold hover:bg-gray-800 transition-colors">Registrar</button>
                        </form>
                    </div>
                </div>

                {/* COLUMNA DERECHA: REGISTROS EN VIVO */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Lista de Empleados */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-lg font-bold text-[#2C3E50] mb-4 flex items-center gap-2">
                            <FaUserTie /> Equipo Registrado
                        </h2>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 text-gray-600 font-bold border-b">
                                    <tr>
                                        <th className="py-2 px-4">Nombre</th>
                                        <th className="py-2 px-4">Cargo</th>
                                        <th className="py-2 px-4 text-center">PIN</th>
                                        <th className="py-2 px-4 text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {empleados.map(emp => (
                                        <tr key={emp.id} className="border-b hover:bg-gray-50">
                                            <td className="py-2 px-4 font-semibold text-gray-800">{emp.nombre}</td>
                                            <td className="py-2 px-4 text-gray-600">{emp.cargo}</td>
                                            <td className="py-2 px-4 text-center font-mono font-bold text-[#18BC9C]">****</td>
                                            <td className="py-2 px-4 text-center">
                                                <button onClick={() => handleEliminarEmpleado(emp.id)} className="text-red-400 hover:text-red-600"><FaTrash /></button>
                                            </td>
                                        </tr>
                                    ))}
                                    {empleados.length === 0 && <tr><td colSpan="4" className="text-center py-4 text-gray-400 italic">No hay trabajadores registrados.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Fichajes en vivo */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-lg font-bold text-[#2C3E50] mb-4 flex items-center gap-2">
                            <FaClock /> Fichajes Recientes (Radar en vivo)
                        </h2>
                        <div className="overflow-x-auto h-96">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-[#2C3E50] text-white font-bold sticky top-0">
                                    <tr>
                                        <th className="py-2 px-4 rounded-tl-lg">Fecha y Hora</th>
                                        <th className="py-2 px-4">Trabajador</th>
                                        <th className="py-2 px-4 text-center">Acción</th>
                                        <th className="py-2 px-4 text-center">Ubicación GPS</th>
                                        <th className="py-2 px-4 rounded-tr-lg text-center"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {registros.map(reg => (
                                        <tr key={reg.id} className="border-b hover:bg-gray-50">
                                            <td className="py-3 px-4 font-mono text-xs text-gray-600">
                                                {reg.timestamp ? new Date(reg.timestamp.toDate()).toLocaleString('es-ES') : 'Cargando...'}
                                            </td>
                                            <td className="py-3 px-4 font-bold text-gray-800">{reg.nombre}</td>
                                            <td className="py-3 px-4 text-center">
                                                {reg.tipo === 'entrada' 
                                                    ? <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold uppercase">Entrada</span>
                                                    : <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold uppercase">Salida</span>
                                                }
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                {reg.ubicacion ? (
                                                    <a href={`https://www.google.com/maps/search/?api=1&query=${reg.ubicacion.lat},${reg.ubicacion.lng}`} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-700 flex items-center justify-center gap-1">
                                                        <FaMapMarkerAlt /> Ver Mapa
                                                    </a>
                                                ) : <span className="text-gray-400 text-xs">Sin GPS</span>}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                {/* 🔥 JARVIS: BOTÓN PARA BORRAR EL REGISTRO */}
                                                <button onClick={() => handleEliminarRegistro(reg.id)} className="text-red-400 hover:text-red-600 transition-colors" title="Eliminar registro">
                                                    <FaTrash size={14}/>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {registros.length === 0 && <tr><td colSpan="5" className="text-center py-4 text-gray-400 italic">No hay registros recientes.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AsistenciaAdminView;